/**
 * useCurrentOrg — resolves the user's "current organization" by combining
 * the URL `:orgSlug` param with the `memberships[]` array on `/auth/me`.
 *
 * Resolution rules:
 *  - Loading / unauthenticated session → `null`
 *  - Zero memberships → `null` (no-org user; onboarding state handled
 *    elsewhere)
 *  - Exactly one membership → that membership, regardless of URL slug
 *    (single-org users always land in their org; the caller can
 *    optionally redirect the URL to the matching slug)
 *  - URL slug matches a membership → that membership
 *  - URL slug is set / unset and the user has multiple memberships,
 *    none of which match → returns the fallback membership (first
 *    accessible org) AND `slugMismatch: true` so URL-canonicalizing
 *    callers can redirect to `/orgs/${membership.organizationSlug}`.
 *
 * Contract (2026-05-11 after SCRUM-58 @serg-erin review):
 *  - `membership` is **always populated** when the user has at least
 *    one accessible org. Callers mounted at `/` render it directly;
 *    callers mounted under `/orgs/:orgSlug` redirect using
 *    `state.membership.organizationSlug`.
 *  - `slugMismatch` is the lone redirect signal — no duplicated
 *    `fallbackSlug` field. (Removed @serg-erin review 2026-05-11:
 *    duplicate state, guaranteed future drift.)
 *  - Earlier shape had `membership: null` on slugMismatch — that
 *    caused DashboardPage to render `null` (blank page) at `/` for
 *    multi-org users. The current shape eliminates that class of bug.
 *
 * Deterministic-org-selection follow-up:
 *  - "First accessible org" is currently `memberships[0]` — a
 *    non-deterministic FE pick by JS array order. Tracked at
 *    `docs/decisions/scrum-58-deterministic-org-selection.md` and
 *    `SCRUM-71` (https://genzai-solutions.atlassian.net/browse/SCRUM-71).
 *    Must settle before SCRUM-61 cements the org switcher.
 */

import { useParams } from 'react-router-dom';
import { useSession } from './useSession.js';

/**
 * SCRUM-69 DS-004-6: each role on a membership is an object pairing the
 * authz `slug` with a human-readable `displayName` (the row's `Role.name`
 * column on the backend). The backend wire shape changed from `string[]`
 * to `Array<{slug, displayName}>` in /auth/me; this hook follows.
 */
export interface CurrentOrgRoleRef {
  readonly slug: string;
  readonly displayName: string;
}

export interface CurrentOrgMembership {
  readonly organizationId: string;
  readonly organizationName: string;
  readonly organizationSlug: string;
  readonly roles: readonly CurrentOrgRoleRef[];
}

export interface CurrentOrgResult {
  readonly membership: CurrentOrgMembership;
  readonly slugMismatch: false;
}

export interface CurrentOrgSlugMismatch {
  /** The fallback membership (first accessible org). Always non-null
      when this shape is returned; callers at `/` render it directly,
      and URL-canonicalizing callers redirect to
      `/orgs/${membership.organizationSlug}`. */
  readonly membership: CurrentOrgMembership;
  readonly slugMismatch: true;
}

export type CurrentOrgState = CurrentOrgResult | CurrentOrgSlugMismatch | null;

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isNonEmptyString(value: unknown): value is string {
  return isString(value) && value.length > 0;
}

/**
 * SCRUM-69 DS-004-6: type guard for the object-form role entries the API
 * now returns. Both `slug` and `displayName` must be non-empty strings;
 * extra fields are tolerated (forward-compat for future `isBuiltIn` etc.)
 * but never surfaced beyond what the interface lists.
 */
function isRoleRef(value: unknown): value is { slug: string; displayName: string } {
  if (value === null || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return isNonEmptyString(obj.slug) && isNonEmptyString(obj.displayName);
}

function isRoleRefArray(value: unknown): value is Array<{ slug: string; displayName: string }> {
  return Array.isArray(value) && value.every(isRoleRef);
}

/**
 * Defensive narrowing — accepts anything looking like a membership row
 * from `/auth/me` (matching the DTO at
 * `apps/api/src/modules/identity/interface/dto/auth-me.response.dto.ts`).
 * Returns `null` for shapes that don't validate.
 */
function toMembership(raw: unknown): CurrentOrgMembership | null {
  if (raw === null || typeof raw !== 'object') return null;
  const m = raw as Record<string, unknown>;
  if (!isNonEmptyString(m.organizationId)) return null;
  if (!isNonEmptyString(m.organizationName)) return null;
  if (!isNonEmptyString(m.organizationSlug)) return null;
  if (!isRoleRefArray(m.roles) || m.roles.length === 0) return null;
  return {
    organizationId: m.organizationId,
    organizationName: m.organizationName,
    organizationSlug: m.organizationSlug,
    // Copy each entry to strip any extra fields the API may surface later.
    roles: m.roles.map((r) => ({ slug: r.slug, displayName: r.displayName })),
  };
}

export function useCurrentOrg(): CurrentOrgState {
  const session = useSession();
  const params = useParams<{ orgSlug?: string }>();

  if (session.status !== 'authenticated') return null;
  const user = session.user;
  if (user === null || typeof user !== 'object') return null;

  const rawMemberships = (user as Record<string, unknown>).memberships;
  if (!Array.isArray(rawMemberships)) return null;

  const memberships = rawMemberships
    .map(toMembership)
    .filter((m): m is CurrentOrgMembership => m !== null);

  // No-org user — onboarding state is handled by the caller.
  if (memberships.length === 0) return null;

  // Single-org user — always land in their org regardless of URL slug.
  if (memberships.length === 1) {
    return {
      membership: memberships[0] as CurrentOrgMembership,
      slugMismatch: false,
    };
  }

  // Multi-org user — match URL slug.
  const urlSlug = params.orgSlug ?? null;
  if (urlSlug !== null) {
    const match = memberships.find((m) => m.organizationSlug === urlSlug);
    if (match !== undefined) {
      return { membership: match, slugMismatch: false };
    }
  }

  // URL slug missing or doesn't match any membership the user has.
  // Populate `membership` with the fallback (first accessible org) so
  // callers at `/` always have something to render. Callers under
  // `/orgs/:orgSlug` derive the redirect target from
  // `state.membership.organizationSlug` — no duplicated `fallbackSlug`
  // field (see @serg-erin review 2026-05-11).
  //
  // CAVEAT: "first accessible org" is currently `memberships[0]` — a
  // non-deterministic FE pick by JS array order. Decision required
  // before SCRUM-61 cements the org switcher. Tracked at
  // `docs/decisions/scrum-58-deterministic-org-selection.md` and
  // SCRUM-71 (https://genzai-solutions.atlassian.net/browse/SCRUM-71).
  //
  // Safe non-null assertion: memberships.length > 1 was already
  // checked by the single-org branch above, and the empty-array case
  // returned null further up.
  const fallback = memberships[0] as CurrentOrgMembership;
  return {
    membership: fallback,
    slugMismatch: true,
  };
}
